// src/components/ui/NotificationBell.jsx
import { useState, useRef, useEffect } from "react";
import useNotifications from "../../hooks/useNotifications";
import useAuth from "../../hooks/useAuth";
import PortalIcon from "./PortalIcon";

const TYPE_COLOR = {
  payment: "#10B981", file: "#6366F1", order: "#F59E0B",
  chat: "#3B82F6", approval: "#10B981", due: "#EF4444", info: "#6366F1",
  success: "#10B981", warning: "#EF4444", system: "#8B5CF6", message: "#3B82F6",
  refund: "#EF4444", support: "#3B82F6",
  branding_payment: "#10B981", branding_generating: "#38BDF8",
  branding_ready: "#10B981", branding_failed: "#EF4444",
  branding_admin_status: "#F59E0B",
};

export default function NotificationBell({ t }) {
  const { user } = useAuth();
  const { notifications, unreadCount, loading, markRead, markAllRead, requestPermission, permission } = useNotifications(user?.uid);
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 70, right: 12 });
  const ref = useRef();

  const T = {
    border:  t?.border  || "rgba(255,255,255,.12)",
    bgCard:  t?.bgCard  || t?.bg2  || "#0F1629",
    bgCard2: t?.bgCard2 || t?.bg3  || "#151E35",
    accent:  t?.accent  || "#6366F1",
    text:    t?.text    || "#F1F5F9",
    muted:   t?.muted   || "#8892A4",
    shadow:  t?.shadow  || "rgba(0,0,0,.45)",
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  // Recalculate position on resize/scroll while open
  useEffect(() => {
    if (!open) return;
    const recalc = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const right = Math.max(8, window.innerWidth - rect.right);
      setDropPos({ top: rect.bottom + 6, right });
    };
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => { window.removeEventListener("resize", recalc); window.removeEventListener("scroll", recalc, true); };
  }, [open]);

  const handleOpen = async (e) => {
    e?.stopPropagation?.();
    // Calculate dropdown position from button's actual rect
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const right = Math.max(8, window.innerWidth - rect.right);
      setDropPos({ top: rect.bottom + 6, right });
    }
    setOpen(v => !v);
  };

  const timeAgo = (ts) => {
    if (!ts?.seconds) return "";
    const diff = Math.floor((Date.now() / 1000) - ts.seconds);
    if (diff < 60)    return "just now";
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const handleClick = (n) => {
    markRead(n.id);
    setOpen(false);
    if (!n.link) return;
    if (/^https?:\/\//i.test(n.link)) window.open(n.link, "_blank", "noopener,noreferrer");
    else window.location.href = n.link;
  };

  const permGranted = permission === "granted";
  const permDenied  = permission === "denied";
  const permDefault = permission === "default";

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      {/* Bell button — no zIndex so it doesn't fight the dropdown */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        style={{
          position: "relative",
          width: 38, height: 38,
          borderRadius: 9,
          border: `1px solid ${T.border}`,
          background: open ? `${T.accent}22` : T.bgCard,
          color: T.text,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.05rem",
          cursor: "pointer",
          transition: "all .2s",
          flexShrink: 0,
        }}
      >
        <PortalIcon name="bell" size={18} />
        {unreadCount > 0 && (
          <span style={{
            position: "absolute", top: -5, right: -5,
            minWidth: 18, height: 18,
            background: "linear-gradient(135deg,#EF4444,#DC2626)",
            color: "#fff", borderRadius: 50,
            fontSize: ".65rem", fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 4px",
            animation: "pulse 2s ease-in-out infinite",
            boxShadow: "0 2px 8px rgba(239,68,68,.5)",
            pointerEvents: "none",
          }}>
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown — higher z-index so it appears ABOVE everything */}
      {open && (
        <div style={{
          position: "fixed",
          top: dropPos.top,
          right: dropPos.right,
          width: "min(340px, calc(100vw - 24px))",
          maxHeight: "min(480px, calc(100dvh - 88px))",
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: `0 20px 60px ${T.shadow}`,
          zIndex: 10010,  // Higher than any other element
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${T.border}`,
            background: T.bgCard2,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
          }}>
            <div>
              <div style={{ fontWeight: 700, color: T.text, fontSize: ".92rem", display: "flex", alignItems: "center", gap: 8 }}>
                <PortalIcon name="bell" size={16} /> Notifications
              </div>
              {unreadCount > 0 && (
                <div style={{ fontSize: ".72rem", color: T.muted }}>{unreadCount} unread</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {unreadCount > 0 && (
                <button type="button" onClick={markAllRead} style={{
                  background: "none", border: `1px solid ${T.border}`,
                  borderRadius: 6, padding: "4px 10px",
                  fontSize: ".74rem", color: T.muted,
                  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                }}>
                  All read
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} style={{
                width: 28, height: 28,
                background: "none", border: `1px solid ${T.border}`,
                borderRadius: 7, color: T.muted,
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", lineHeight: 1,
              }}>×</button>
            </div>
          </div>

          {/* Permission banners */}
          {permDefault && (
            <div style={{ padding: "10px 14px", background: `${T.accent}10`, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: ".8rem", color: T.muted, flex: 1 }}>Enable browser notifications?</span>
              <button type="button" onClick={requestPermission} style={{
                padding: "5px 12px",
                background: `linear-gradient(135deg,${T.accent},#4F46E5)`,
                color: "#fff", border: "none", borderRadius: 7,
                fontSize: ".76rem", fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
              }}>Enable</button>
            </div>
          )}
          {permDenied && (
            <div style={{ padding: "9px 14px", background: "rgba(239,68,68,.08)", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: ".76rem", color: "#EF4444", display: "flex", alignItems: "center", gap: 6 }}>
                <PortalIcon name="info" size={14} /> Browser notifications blocked. Enable in browser settings.
              </div>
            </div>
          )}

          {/* Notification list */}
          <div style={{ maxHeight: "min(360px, calc(100dvh - 230px))", overflowY: "auto", overscrollBehavior: "contain" }}>
            {loading ? (
              <div style={{ padding: "40px 20px", textAlign: "center" }}>
                <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 12 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: T.accent, opacity: 0.7, animation: `dgh-dot-pl 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
                <div style={{ color: T.muted, fontSize: ".82rem" }}>Loading...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: "40px 20px", textAlign: "center" }}>
                <PortalIcon name="bell" size={36} style={{ marginBottom: 10, color: T.muted }} />
                <div style={{ color: T.muted, fontSize: ".87rem" }}>No notifications yet</div>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  style={{
                    padding: "12px 16px",
                    borderBottom: `1px solid ${T.border}`,
                    background: n.read ? "transparent" : `${TYPE_COLOR[n.type] || "#6366F1"}08`,
                    cursor: "pointer",
                    display: "flex", gap: 11, alignItems: "flex-start",
                    transition: "background .15s",
                    borderLeft: `3px solid ${n.read ? "transparent" : TYPE_COLOR[n.type] || "#6366F1"}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = T.bgCard2; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.read ? "transparent" : `${TYPE_COLOR[n.type] || "#6366F1"}08`; }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%",
                    background: `${TYPE_COLOR[n.type] || "#6366F1"}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <PortalIcon
                      name={
                        n.type === "payment" || n.type === "branding_payment" ? "card" :
                        n.type === "file"    ? "file" :
                        n.type === "order"   ? "package" :
                        n.type === "chat"    ? "chat" :
                        n.type === "message" ? "chat" :
                        n.type === "success" || n.type === "branding_ready"   ? "check" :
                        n.type === "system"  ? "settings" :
                        n.type === "warning" || n.type === "branding_failed"  ? "info" :
                        n.type === "refund"  ? "refresh" :
                        n.type === "support" ? "shield" :
                        n.type === "approval"? "check" :
                        n.type === "branding_generating" ? "refresh" :
                        n.type === "branding_admin_status" ? "shield" :
                        "info"
                      }
                      size={16}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: n.read ? 400 : 700, color: T.text, fontSize: ".86rem", marginBottom: 2, lineHeight: 1.3 }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: ".77rem", color: T.muted, lineHeight: 1.4 }}>{n.body}</div>
                    <div style={{ fontSize: ".69rem", color: T.muted, marginTop: 3 }}>{timeAgo(n.createdAt)}</div>
                  </div>
                  {!n.read && (
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: TYPE_COLOR[n.type] || "#6366F1", flexShrink: 0, marginTop: 4 }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}`, background: T.bgCard2, textAlign: "center" }}>
            <div style={{ fontSize: ".72rem", color: T.muted }}>
              {permGranted ? "✓ Browser notifications enabled" : "Notifications from DG HelpMate"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
